import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  Plus, 
  Search, 
  Filter, 
  Send, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Eye,
  MoreVertical,
  ArrowRight,
  Loader2,
  FileText,
  ExternalLink,
  Copy,
  Mail,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ProposalsPage
 * =============
 * Dashboard for managing client proposals.
 * Designer/Architect view to create, track, and manage proposals.
 * 
 * Dark luxury UI consistent with SlabReality design.
 */
export default function ProposalsPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchProposals();
  }, [statusFilter]);

  const fetchProposals = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/proposals?limit=50`;
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      
      const response = await fetch(url, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        const data = await response.json();
        if (data.error === 'TABLE_NOT_FOUND') {
          // Tables not created yet
          setProposals([]);
          return;
        }
        throw new Error('Errore nel caricamento');
      }
      
      const data = await response.json();
      setProposals(data.proposals || []);
      
    } catch (err) {
      console.error('Fetch proposals error:', err);
      toast.error('Errore nel caricamento delle proposte');
    } finally {
      setLoading(false);
    }
  };

  const filteredProposals = proposals.filter(p => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      p.title?.toLowerCase().includes(query) ||
      p.client_email?.toLowerCase().includes(query) ||
      p.client_name?.toLowerCase().includes(query) ||
      p.project_name?.toLowerCase().includes(query)
    );
  });

  const handleSendProposal = async (proposalId) => {
    if (!window.confirm('Inviare la proposta al cliente?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/proposals/${proposalId}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
      if (!response.ok) throw new Error('Errore nell\'invio');
      
      const data = await response.json();
      toast.success('Proposta inviata!');
      
      // Copy link to clipboard
      await navigator.clipboard.writeText(data.client_link);
      toast.info('Link copiato negli appunti');
      
      fetchProposals();
      
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleCopyLink = async (proposal) => {
    const link = `${window.location.origin}/client/proposal/${proposal.access_token}`;
    await navigator.clipboard.writeText(link);
    toast.success('Link copiato negli appunti');
  };

  const statusCounts = {
    all: proposals.length,
    draft: proposals.filter(p => p.status === 'draft').length,
    sent: proposals.filter(p => p.status === 'sent').length,
    viewed: proposals.filter(p => p.status === 'viewed').length,
    approved: proposals.filter(p => p.status === 'approved').length,
    rejected: proposals.filter(p => p.status === 'rejected').length
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <div className="border-b border-zinc-800/50 bg-[#0A0A0B]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-light tracking-wide">Proposte</h1>
              <p className="text-zinc-500 mt-1">Gestisci le proposte per i tuoi clienti</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-[#B08B5C] to-[#C8A36A] text-black font-medium rounded-lg hover:brightness-110 transition-all flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Nuova Proposta
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Status Tabs */}
          <div className="flex gap-2 flex-wrap">
            {[
              { key: 'all', label: 'Tutte' },
              { key: 'draft', label: 'Bozze' },
              { key: 'sent', label: 'Inviate' },
              { key: 'viewed', label: 'Viste' },
              { key: 'approved', label: 'Approvate' },
              { key: 'rejected', label: 'Rifiutate' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm transition-all ${
                  statusFilter === tab.key
                    ? 'bg-[#B08B5C] text-black'
                    : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800'
                }`}
              >
                {tab.label}
                <span className="ml-2 text-xs opacity-60">({statusCounts[tab.key]})</span>
              </button>
            ))}
          </div>
          
          {/* Search */}
          <div className="flex-1 md:max-w-xs ml-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca..."
                className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#B08B5C]"
              />
            </div>
          </div>
        </div>

        {/* Proposals List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#B08B5C] animate-spin" />
          </div>
        ) : filteredProposals.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-zinc-400 mb-2">
              {searchQuery ? 'Nessun risultato' : 'Nessuna proposta'}
            </h3>
            <p className="text-zinc-600 mb-6">
              {searchQuery 
                ? 'Prova a modificare la ricerca'
                : 'Crea la tua prima proposta per iniziare'
              }
            </p>
            {!searchQuery && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-[#B08B5C] text-black font-medium rounded-lg hover:brightness-110 transition-all inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Nuova Proposta
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredProposals.map((proposal) => (
              <ProposalCard 
                key={proposal.id} 
                proposal={proposal}
                onSend={() => handleSendProposal(proposal.id)}
                onCopyLink={() => handleCopyLink(proposal)}
                onView={() => navigate(`/proposals/${proposal.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateProposalModal 
          onClose={() => setShowCreateModal(false)}
          onCreated={(newProposal) => {
            setProposals(prev => [newProposal, ...prev]);
            setShowCreateModal(false);
          }}
          token={token}
        />
      )}
    </div>
  );
}

// Proposal Card Component
function ProposalCard({ proposal, onSend, onCopyLink, onView }) {
  const [showMenu, setShowMenu] = useState(false);
  
  const statusConfig = {
    draft: { icon: FileText, color: 'text-zinc-400', bg: 'bg-zinc-700' },
    sent: { icon: Send, color: 'text-[var(--gold)]', bg: 'bg-[var(--gold)]/20' },
    viewed: { icon: Eye, color: 'text-purple-400', bg: 'bg-purple-500/20' },
    approved: { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/20' },
    rejected: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/20' },
    revision_requested: { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/20' }
  };
  
  const status = statusConfig[proposal.status] || statusConfig.draft;
  const StatusIcon = status.icon;

  return (
    <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-6 hover:border-zinc-700 transition-all group">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-medium text-white">{proposal.title}</h3>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
              <StatusIcon className="w-3 h-3 inline mr-1" />
              {proposal.status}
            </span>
          </div>
          
          <div className="flex flex-wrap gap-4 text-sm text-zinc-500">
            <span>{proposal.client_name || proposal.client_email}</span>
            {proposal.project_name && (
              <span className="flex items-center gap-1">
                <span className="w-1 h-1 rounded-full bg-zinc-600" />
                {proposal.project_name}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(proposal.created_at).toLocaleDateString('it-IT')}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          {proposal.status === 'draft' && (
            <button
              onClick={onSend}
              className="px-4 py-2 bg-[#B08B5C] text-black text-sm font-medium rounded-lg hover:brightness-110 flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              Invia
            </button>
          )}
          
          {proposal.status !== 'draft' && (
            <button
              onClick={onCopyLink}
              className="px-4 py-2 bg-zinc-800 border border-zinc-700 text-sm rounded-lg hover:bg-zinc-700 flex items-center gap-2"
            >
              <Copy className="w-4 h-4" />
              Copia Link
            </button>
          )}
          
          <button
            onClick={onView}
            className="px-4 py-2 bg-zinc-800 border border-zinc-700 text-sm rounded-lg hover:bg-zinc-700 flex items-center gap-2"
          >
            Dettagli
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Create Proposal Modal
function CreateProposalModal({ onClose, onCreated, token }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client_email: '',
    client_name: '',
    project_name: '',
    project_type: 'residential',
    expires_in_days: 30
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.client_email) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/proposals/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) throw new Error('Errore nella creazione');
      
      const data = await response.json();
      toast.success('Proposta creata!');
      onCreated(data.proposal);
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-[#0F0F10] border border-zinc-800 rounded-2xl w-full max-w-lg p-6">
        <h2 className="text-xl font-medium mb-6">Nuova Proposta</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Titolo *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="es. Proposta Cucina Villa Rossi"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#B08B5C]"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Email Cliente *</label>
              <input
                type="email"
                value={formData.client_email}
                onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                placeholder="cliente@example.com"
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#B08B5C]"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Nome Cliente</label>
              <input
                type="text"
                value={formData.client_name}
                onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                placeholder="Marco Rossi"
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#B08B5C]"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Nome Progetto</label>
              <input
                type="text"
                value={formData.project_name}
                onChange={(e) => setFormData(prev => ({ ...prev, project_name: e.target.value }))}
                placeholder="Villa Rossi - Milano"
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#B08B5C]"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Tipo Progetto</label>
              <select
                value={formData.project_type}
                onChange={(e) => setFormData(prev => ({ ...prev, project_type: e.target.value }))}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#B08B5C]"
              >
                <option value="residential">Residenziale</option>
                <option value="commercial">Commerciale</option>
                <option value="hospitality">Hospitality</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Descrizione</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Descrizione della proposta..."
              rows={3}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#B08B5C] resize-none"
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-zinc-400 hover:text-white transition-colors"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-3 bg-gradient-to-r from-[#B08B5C] to-[#C8A36A] text-black font-medium rounded-lg hover:brightness-110 disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Plus className="w-5 h-5" />
              )}
              Crea Proposta
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
